import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../UIPalette/components/PageHeader/PageHeader";
import {
  AdminUsersResponseModel,
  ListAdminUsersQuery,
  useDeleteClientAdminUserFunctionRequest,
  useEraseUserFunctionRequest,
  useListAdminUsersFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { useCallback, useEffect, useState } from "react";
import { RowMenuItem, Table, TableHeader } from "../../../UIPalette/components/Table/Table";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { OrderByDirection, RoleKeys, Roles } from "core/Constants";
import { useDebounce } from "shared/core/hooks/useDebounce";
import { ClientDivisionSelection, ClientDivisionSelector } from "../../../UIPalette/components/ClientDivisionSelector/ClientDivisionSelector";
import Button from "shared/UI/Buttons/Button";
import "shared/extensions/string";
import { useNavigate } from "react-router-dom";
import { useAdminUserContext } from "core/state/AdminUserContext";
import { ListAdminUsersOrderBy } from "./ListAdminUsersOrderBy";
import { ConfirmationModal } from "shared/UI/Modal/ConfirmationModal/ConfirmationModal";
import { Input } from "shared/UI/Input/Input";
import { ReactComponent as SearchIcon } from "@icons/magnifying-glass.svg";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";

export const ViewClientAdmins = () => {
  const { t } = useTranslation();
  const { modalNotify } = useNotifyModalContext();
  const navigateHook = useNavigate();
  const { trigger: listAdminUsersRequest } = useListAdminUsersFunctionRequest();
  const { trigger: deleteAdminUserRequest } = useDeleteClientAdminUserFunctionRequest();
  const { trigger: eraseUserRequest } = useEraseUserFunctionRequest();
  const [userResponse, setUserResponse] = useState<AdminUsersResponseModel>();
  const [errorMessage, setErrorMessage] = useState("");
  const [ordering, setOrdering] = useState({
    column: ListAdminUsersOrderBy.CreatedDateUtc,
    direction: OrderByDirection.Descending,
  });
  const [paging, setPaging] = useState({ current: 1, size: 10 });
  const [clientDivisionFilter, setClientDivisionFilter] = useState<ClientDivisionSelection>();
  const [keywordFilter, setKeywordFilter] = useState<string>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEraseModal, setShowEraseModal] = useState(false);
  const [selectedAdminUserIndex, setSelectedAdminUserIndex] = useState(0);
  const debouncedKeyword = useDebounce(keywordFilter, 500);
  const { isAuthorised } = useAdminUserContext();

  const tableHeaders: TableHeader[] = [
    {
      text: t("clientAdminManagement.table.nameHeading"),
      orderByValue: ListAdminUsersOrderBy.Forenames,
    },
    {
      text: t("clientAdminManagement.table.surnameHeading"),
      orderByValue: ListAdminUsersOrderBy.Surname,
    },
    {
      text: t("clientAdminManagement.table.emailHeading"),
      orderByValue: ListAdminUsersOrderBy.Email,
    },
    {
      text: t("clientAdminManagement.table.clientHeading"),
      orderByValue: ListAdminUsersOrderBy.Client,
    },
    {
      text: t("clientAdminManagement.table.divisionHeading"),
      orderByValue: ListAdminUsersOrderBy.Division,
    },
    {
      text: t("clientAdminManagement.table.statusHeading"),
      orderByValue: ListAdminUsersOrderBy.IsActive,
    },
    {
      text: t("clientAdminManagement.table.creationDateHeading"),
      orderByValue: ListAdminUsersOrderBy.CreatedDateUtc,
      defaultOrderDirection: OrderByDirection.Descending,
    },
  ];

  const buildRowMenuItems = () => {
    let rowMenuItems: RowMenuItem[] = [];

    if (isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["delete-client-admins"] })) {
      rowMenuItems.push({
        title: t("table.deleteTitle"),
        "aria-label": t("table.ariaDeleteRow"),
        onClick: (rowIndex: number) => onDeleteAdminUser(rowIndex),
      });
    }

    if (isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["edit-client-admins"] })) {
      rowMenuItems.push({
        title: t("table.editTitle"),
        "aria-label": t("table.ariaEditRow"),
        onClick: (rowIndex: number) => onEditAdminUser(rowIndex),
      });
    }

    if (isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["erase-users"] })) {
      rowMenuItems.push({
        title: t("table.eraseTitle"),
        "aria-label": t("table.ariaEraseRow"),
        onClick: (row: number) => onEraseAdminUser(row),
      });
    }

    return rowMenuItems;
  };

  const mapTableData = () =>
    userResponse?.records?.map(({ forenames, surname, email, client, division, isActive, ...rest }) => {
      return {
        forenames,
        surname,
        email,
        client: client?.name,
        division: division?.name,
        status: isActive ? t("wwAdminManagement.status.active") : t("wwAdminManagement.status.pending"),
        createdDate: String.formatDateTime(rest.createdDate),
      };
    });

  const deleteAdminUser = async () => {
    try {
      const userId = userResponse!.records![selectedAdminUserIndex].id;
      await deleteAdminUserRequest({ query: { id: userId } });
      modalNotify("success", t("adminDelete.successTitle"), t("adminDelete.successMessage"));
      fetchAdminUsersList();
    } catch {
      setErrorMessage(t("adminDelete.client.requestErrorMessage"));
    } finally {
      setShowDeleteModal(false);
    }
  };

  const eraseUser = async () => {
    try {
      const targetUser = userResponse!.records![selectedAdminUserIndex];
      await eraseUserRequest({ body: { email: targetUser.email } });
      fetchAdminUsersList();
      modalNotify("success", t("eraseModal.successfulTitle"), t("eraseModal.successfulErase"));
    } catch {
      setErrorMessage(t("eraseModal.failedErase"));
    } finally {
      setShowEraseModal(false);
    }
  };

  const fetchAdminUsersList = useCallback(() => {
    async function listClientAdminUsers() {
      const requestBody: ListAdminUsersQuery = {
        roleId: Roles.ClientAdmin,
        page: paging.current,
        perPage: paging.size,
        orderBy: ordering.column,
        orderByDirection: ordering.direction,
        filter: {
          keyword: debouncedKeyword,
          clientId: clientDivisionFilter?.client?.id,
          divisionId: clientDivisionFilter?.division?.id,
        },
      };
      try {
        setErrorMessage("");
        const response = await listAdminUsersRequest({ body: requestBody });
        setUserResponse(response?.data);
      } catch {
        setErrorMessage(t("clientAdminManagement.requestErrorMessage"));
      }
    }

    listClientAdminUsers();
  }, [
    clientDivisionFilter?.client?.id,
    clientDivisionFilter?.division?.id,
    debouncedKeyword,
    listAdminUsersRequest,
    ordering.column,
    ordering.direction,
    paging,
    t,
  ]);

  useEffect(() => {
    fetchAdminUsersList();
  }, [fetchAdminUsersList]);

  const onDeleteAdminUser = async (rowIndex: number) => {
    setSelectedAdminUserIndex(rowIndex);
    setShowDeleteModal(true);
  };

  const onEraseAdminUser = async (rowIndex: number) => {
    setSelectedAdminUserIndex(rowIndex);
    setShowEraseModal(true);
  };

  const onEditAdminUser = async (rowIndex: number) => {
    navigateHook("/usermanagement/clientadmins/edit", { state: userResponse!.records![rowIndex] });
  };

  return (
    <>
      <PageHeader title={t("clientAdminManagement.title")} subtitle={t("clientAdminManagement.subtitle")} />
      <ClientDivisionSelector onSelection={(clientDivisionFilters) => setClientDivisionFilter(clientDivisionFilters)} />
      <Input
        aria-label={t("clientAdminManagement.ariaSearch")}
        onChange={(a) => setKeywordFilter(a.target.value)}
        icon={<SearchIcon />}
        placeholder={t("clientAdminManagement.searchPlaceholder")}
        label={t("clientAdminManagement.searchLabel")}
      />

      <Table
        headers={tableHeaders}
        dataRows={mapTableData() ?? []}
        orderedByColumn={ordering.column}
        orderedDirection={ordering.direction}
        currentPage={paging.current}
        pageSize={paging.size}
        totalDataCount={userResponse?.metadata?.totalCount ?? 0}
        onOrderingChange={setOrdering}
        onPagingChange={setPaging}
        actionButtons={<Button text={"+"} onClick={() => navigateHook("/usermanagement/clientadmins/create")} />}
        rowMenuItems={buildRowMenuItems()}
      />
      {showDeleteModal && (
        <ConfirmationModal
          title={t("adminDelete.client.modalTitle")}
          message={t("adminDelete.modalMessage", { userEmail: userResponse!.records![selectedAdminUserIndex].email })}
          cancelButtonText={t("adminDelete.modalDismissTitle")}
          confirmButtonText={t("adminDelete.modalConfirmTitle")}
          onCancel={() => {
            setShowDeleteModal(false);
          }}
          onConfirm={deleteAdminUser}
        />
      )}
      {showEraseModal && (
        <ConfirmationModal
          title={t("eraseModal.title")}
          message={t("eraseModal.message", { userEmail: userResponse!.records![selectedAdminUserIndex].email })}
          cancelButtonText={t("eraseModal.cancel")}
          confirmButtonText={t("eraseModal.confirm")}
          onCancel={() => {
            setShowEraseModal(false);
          }}
          onConfirm={eraseUser}
        />
      )}
      <ErrorAlert content={errorMessage} />
    </>
  );
};
